import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

// Auto-detect API URL based on current domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  
  // Production
  if (hostname === 'aquanexia.com' || hostname === 'www.aquanexia.com') {
    return 'https://aquanexia.com/api';
  }
  
  // Preview/Development
  if (process.env.REACT_APP_BACKEND_URL) {
    return `${process.env.REACT_APP_BACKEND_URL}/api`;
  }
  
  // Fallback to same origin
  return `${window.location.origin}/api`;
};

const API_URL = getApiUrl();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionValid, setSubscriptionValid] = useState(true);
  const [subscriptionMessage, setSubscriptionMessage] = useState(null);
  const [requiresSubscription, setRequiresSubscription] = useState(false);

  // Create axios instance
  const api = axios.create({
    baseURL: API_URL
  });

  // Add token to all requests
  api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Initialize - check if we have a valid token
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Use full endpoint to get user info with subscription status
        const response = await api.get('/auth/me/full');
        const data = response.data;
        
        setUser(data.user);
        setSubscriptionValid(data.subscription_valid !== false);
        setSubscriptionMessage(data.subscription_message);
        setRequiresSubscription(!data.subscription_valid && data.user.role === 'owner');
      } catch (error) {
        // Token invalid - clear it silently
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const data = response.data;
    
    localStorage.setItem('token', data.access_token);
    setUser(data.user);
    setSubscriptionValid(data.subscription_valid !== false);
    setSubscriptionMessage(data.subscription_message);
    setRequiresSubscription(!data.subscription_valid && data.user.role === 'owner');
    
    return data;
  };

  const register = async (userData) => {
    const response = await api.post('/auth/register', userData);
    const data = response.data;
    
    localStorage.setItem('token', data.access_token);
    setUser(data.user);
    setSubscriptionValid(data.subscription_valid !== false);
    setSubscriptionMessage(data.subscription_message);
    setRequiresSubscription(!data.subscription_valid && data.user.role === 'owner');
    
    return data;
  };

  const acceptInvite = async (inviteData) => {
    const response = await api.post('/workers/accept-invite', inviteData);
    const data = response.data;
    
    localStorage.setItem('token', data.access_token);
    setUser(data.user);
    
    return data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setSubscriptionValid(true);
    setSubscriptionMessage(null);
    setRequiresSubscription(false);
  };

  const clearSubscriptionError = () => {
    setRequiresSubscription(false);
    setSubscriptionMessage(null);
  };

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      login,
      register,
      acceptInvite,
      logout,
      api,
      isOwner: user?.role === 'owner',
      isWorker: user?.role === 'worker',
      isAuthenticated: !!user,
      subscriptionValid,
      subscriptionMessage,
      requiresSubscription,
      clearSubscriptionError
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
